/**
 * Known SKILL.md / subskill frontmatter fields used by agent-runtime.
 * The structured editor renders these with typed controls; unknown keys
 * stay in an "Extra fields" section.
 */

export const APPLIES_TO_OPTIONS = [
  "pdp",
  "plp",
  "home",
  "cart",
  "checkout",
  "search",
  "category",
  "account",
  "content",
];

export const KIND_OPTIONS = ["bundle"];

export const SOURCE_OPTIONS = ["custom", "default"];

export const THINKING_OPTIONS = ["off", "low", "medium", "high"];

export const SEVERITY_OPTIONS = ["low", "medium", "high"];

export const LOGO_TYPE_OPTIONS = [
  "product_info",
  "broken_image",
  "broken_link",
  "copy",
  "image",
  "information_check",
  "merchandising",
];

/** Fields the structured editor knows how to render. */
export const KNOWN_FIELD_DEFS = [
  {
    key: "name",
    label: "Name",
    type: "string",
    hint: "Skill / subskill identifier (usually matches folder name).",
  },
  {
    key: "description",
    label: "Description",
    type: "textarea",
    hint: "One-line (or short) summary shown to orchestrators and the Hub.",
  },
  {
    key: "kind",
    label: "Kind",
    type: "select",
    options: KIND_OPTIONS,
    allowCustom: true,
    hint: "Usually `bundle` for orchestrator entry-points.",
  },
  {
    key: "version",
    label: "Version",
    type: "string",
    hint: "e.g. V1, V2 — bumped when behaviour changes meaningfully.",
  },
  {
    key: "source",
    label: "Source",
    type: "select",
    options: SOURCE_OPTIONS,
    allowCustom: true,
    hint: "`custom` for website-scoped skills; `default` for shared bundles.",
  },
  {
    key: "bundle",
    label: "Bundle",
    type: "string",
    hint: "Parent bundle id for custom/exploratory skills (replay/amplify scope).",
  },
  {
    key: "applies_to",
    label: "Applies to",
    type: "string_list",
    suggestions: APPLIES_TO_OPTIONS,
    hint: "Surfaces this skill targets (pdp, plp, …).",
  },
  {
    key: "subskills",
    label: "Subskills (simple list)",
    type: "string_list",
    hint: "Leaf body names under `subskills/`. Prefer `sub_skills` when declaring gates.",
  },
  {
    key: "sub_skills",
    label: "Sub-skills + gates",
    type: "sub_skills",
    hint: "B7 gate coverage: each subskill lists `{id, name}` gates the worker must cover.",
  },
  {
    key: "zenyt_issue_defaults",
    label: "Issue defaults",
    type: "issue_defaults",
    hint: "Default logo_type + severity when the skill publishes findings.",
  },
  {
    key: "model",
    label: "Model",
    type: "string",
    hint: "Optional worker model override (string id).",
  },
  {
    key: "thinking",
    label: "Thinking",
    type: "select",
    options: THINKING_OPTIONS,
    allowCustom: true,
  },
  {
    key: "required_inputs",
    label: "Required inputs",
    type: "string_list",
  },
  {
    key: "required_toolsets",
    label: "Required toolsets",
    type: "json",
    hint: "Map of toolset → scope, e.g. `{ \"browsing\": \"read\" }`.",
  },
  {
    key: "exploratory",
    label: "Exploratory",
    type: "exploratory",
    hint: "Optional discovery/synthesis agents + issue_family guardrail.",
  },
  {
    key: "actions_on_start",
    label: "Actions on start",
    type: "json",
    hint: "Pre-LLM tool steps: `[{ tool, args }]` merged after agent actions_on_start.",
  },
  {
    key: "viewport_pin",
    label: "Viewport pin",
    type: "json",
    hint: "Deterministic page × viewport fan-out matrix for responsive QA skills.",
  },
  {
    key: "journeys",
    label: "Journeys",
    type: "journeys",
    hint: "When required, worker cannot exit cleanly without submit_test_result.",
  },
  {
    key: "publish",
    label: "Publish config",
    type: "json",
  },
  {
    key: "rationale",
    label: "Rationale",
    type: "textarea",
    hint: "Why this custom skill was created (explorer write-skill).",
  },
  {
    key: "issue_schema",
    label: "Issue schema",
    type: "json",
  },
];

export const KNOWN_KEYS = new Set(KNOWN_FIELD_DEFS.map((d) => d.key));

export function emptyValueForType(type) {
  switch (type) {
    case "string_list":
      return [];
    case "sub_skills":
      return [{ name: "", gates: [{ id: "", name: "" }] }];
    case "issue_defaults":
      return { logo_type: "product_info", severity: "medium" };
    case "exploratory":
      return { discovery: "", synthesis: "", issue_family: "" };
    case "journeys":
      return { required: false };
    case "json":
      return {};
    case "textarea":
    case "string":
    case "select":
    default:
      return "";
  }
}

export function normalizeFrontmatter(fm) {
  const src = fm && typeof fm === "object" ? fm : {};
  const known = {};
  const extra = {};
  for (const [key, value] of Object.entries(src)) {
    if (String(key).startsWith("_")) continue;
    if (KNOWN_KEYS.has(key)) known[key] = value;
    else extra[key] = value;
  }
  return { known, extra };
}
